'use client';

import { Download, ChevronLeft } from 'lucide-react';
import Link from 'next/link';
import Button from '@/components/ui/Button';
import FadeIn from '@/components/ui/FadeIn';
import ReadingProgress from '@/components/ui/ReadingProgress';
import HowToExport from '@/components/sections/HowToExport';
import TrustStrip from '@/components/sections/TrustStrip';
import FAQ from '@/components/sections/FAQ';
import TrustSignals from '@/components/sections/TrustSignals';
import FormatSelectorPreview from '@/components/sections/FormatSelectorPreview';
import RelatedUseCases from '@/components/landing/RelatedUseCases';

interface StatItem {
    label: string;
    value: string;
    accent?: boolean;
}

interface LandingArticleProps {
    title: string;
    desc: string;
    breadcrumbTitle: string;
    ctaLabel: string;
    currentPath: string;
    statsItems: [StatItem, StatItem, StatItem, StatItem];
    contentTitle: string;
    contentHtml: string;
}

const TOC_ITEMS = [
    { id: 'how-it-works', title: 'How It Works' },
    { id: 'why-wavault', title: 'Why Use WaVault' },
    { id: 'faq', title: 'Frequently Asked Questions' },
];

const CHROME_STORE_URL =
    'https://chromewebstore.google.com/detail/wa-contacts-extractor-wav/dembjmabcapfkbnbbcphbfcjoghlkcoi';

export default function LandingArticle({
    title,
    desc,
    breadcrumbTitle,
    ctaLabel,
    currentPath,
    statsItems,
    contentTitle,
    contentHtml,
}: LandingArticleProps) {
    const scrollTo = (id: string) => {
        const el = document.getElementById(id);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    return (
        <>
            <ReadingProgress />

            <main className="min-h-screen bg-[var(--color-md-surface)] relative">

                {/* ── HERO ─────────────────────────────────────────── */}
                <section className="relative pt-36 pb-24 md:pt-44 md:pb-24 overflow-hidden">
                    {/* Ambient glow */}
                    <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full bg-[var(--color-md-primary)]/[0.04] blur-[120px] pointer-events-none" />

                    <div className="container mx-auto px-6 max-w-5xl relative z-10">
                        {/* Breadcrumb */}
                        <FadeIn>
                            <nav aria-label="Breadcrumb" className="mb-12 md:mb-16">
                                <ol className="flex items-center gap-2 text-sm font-medium text-[var(--color-md-on-surface-variant)]">
                                    <li>
                                        <Link href="/" className="hover:text-[var(--color-md-primary)] transition-colors">
                                            Home
                                        </Link>
                                    </li>
                                    <li>
                                        <ChevronLeft className="w-3.5 h-3.5 rotate-180 opacity-50" />
                                    </li>
                                    <li>
                                        <span className="text-[var(--color-md-on-surface)]" aria-current="page">
                                            <span className="block sm:hidden">{breadcrumbTitle.substring(0, 24)}...</span>
                                            <span className="hidden sm:block">{breadcrumbTitle}</span>
                                        </span>
                                    </li>
                                </ol>
                            </nav>
                        </FadeIn>

                        <FadeIn delay={0.05}>
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[var(--color-md-primary)]/[0.08] mb-8">
                                <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-md-primary)]" />
                                <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--color-md-primary)]">
                                    WaVault Tool
                                </span>
                            </div>
                        </FadeIn>

                        <FadeIn delay={0.1}>
                            <h1 className="text-[clamp(2.25rem,5vw,4rem)] font-black text-[var(--color-md-on-surface)] tracking-[-0.035em] leading-[1.08] mb-8 max-w-[24ch]">
                                {title}
                            </h1>
                        </FadeIn>

                        <FadeIn delay={0.15}>
                            <p className="text-lg md:text-xl text-[var(--color-md-on-surface-variant)] leading-relaxed max-w-[52ch] mb-12 font-medium">
                                {desc}
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <Button
                                href={CHROME_STORE_URL}
                                target="_blank"
                                className="px-10 py-4 text-base font-bold rounded-full btn-glow"
                            >
                                {ctaLabel}
                            </Button>
                        </FadeIn>
                    </div>
                </section>

                {/* ── TWO COLUMN LAYOUT ────────────────────────────── */}
                <div className="container mx-auto px-6 max-w-5xl pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-12 lg:gap-24 relative">

                    {/* LEFT: Sticky Sidebar */}
                    <aside className="hidden lg:block relative">
                        <div className="sticky top-32">
                            {/* Table of Contents */}
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-[var(--color-md-on-surface-variant)] mb-6 opacity-60">
                                On this page
                            </h4>
                            <nav className="flex flex-col gap-3.5 mb-12">
                                {TOC_ITEMS.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => scrollTo(item.id)}
                                        className="text-[13px] font-bold leading-snug transition-all duration-300 border-l-2 pl-4 py-1 border-transparent text-[var(--color-md-on-surface-variant)] hover:text-[var(--color-md-on-surface)] hover:border-[var(--color-md-outline)] text-left"
                                    >
                                        {item.title}
                                    </button>
                                ))}
                            </nav>

                            {/* Chrome CTA Widget */}
                            <div className="p-6 rounded-3xl bg-[var(--color-md-surface-variant)]/50 border border-[var(--color-md-outline)] shadow-sm">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-8 h-8 rounded-full bg-[var(--color-md-primary)]/[0.1] flex items-center justify-center">
                                        <Download className="w-4 h-4 text-[var(--color-md-primary)]" />
                                    </div>
                                    <span className="text-sm font-bold text-[var(--color-md-on-surface)]">Ready to export?</span>
                                </div>
                                <p className="text-xs text-[var(--color-md-on-surface-variant)] mb-4 font-medium leading-relaxed">
                                    Join 1,000+ professionals exporting WhatsApp contacts privately.
                                </p>
                                <Button
                                    href={CHROME_STORE_URL}
                                    variant="outline"
                                    className="w-full text-xs py-2.5 h-auto rounded-xl"
                                >
                                    Add to Chrome
                                </Button>
                            </div>
                        </div>
                    </aside>

                    {/* RIGHT: Main Content */}
                    <article className="min-w-0">
                        {/* Divider Top */}
                        <div className="h-px bg-[var(--color-md-outline)] mb-12 sm:mb-16" />

                        {/* Format Preview */}
                        <FadeIn>
                            <FormatSelectorPreview />
                        </FadeIn>

                        {/* Stats Strip */}
                        <FadeIn>
                            <section id="how-it-works" className="scroll-mt-32 py-10 my-10 bg-[var(--color-md-surface-variant)]/30 border-y border-[var(--color-md-outline-variant)] rounded-2xl px-6">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                                    {statsItems.map((stat, i) => (
                                        <div key={i} className="text-center">
                                            <div className="text-xs font-bold uppercase tracking-wider text-[var(--color-md-on-surface-variant)] mb-2">
                                                {stat.label}
                                            </div>
                                            <div className={`font-bold ${stat.accent ? 'text-[var(--color-md-primary)]' : 'text-[var(--color-md-on-surface)]'}`}>
                                                {stat.value}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </FadeIn>

                        {/* How To Export */}
                        <FadeIn delay={0.05}>
                            <HowToExport />
                        </FadeIn>

                        {/* Prose Content */}
                        <FadeIn delay={0.1}>
                            <section id="why-wavault" className="scroll-mt-32 mt-12">
                                <div className="prose prose-lg max-w-none text-[var(--color-md-on-surface)]/90
                                    prose-p:leading-relaxed
                                    prose-h2:font-black prose-h2:tracking-tight prose-h2:text-[var(--color-md-on-surface)] prose-h2:mt-12 prose-h2:mb-6
                                    prose-h3:font-bold prose-h3:tracking-tight prose-h3:text-[var(--color-md-on-surface)]
                                    prose-strong:font-black prose-strong:text-[var(--color-md-on-surface)]
                                    prose-li:marker:text-[var(--color-md-primary)]">
                                    <h2>{contentTitle}</h2>
                                    <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
                                </div>
                            </section>
                        </FadeIn>

                        {/* FAQ */}
                        <FadeIn delay={0.1}>
                            <section id="faq" className="scroll-mt-32 mt-12">
                                <FAQ />
                            </section>
                        </FadeIn>
                    </article>
                </div>

                {/* ── BOTTOM CTA BANNER ─────────────────────────────── */}
                <section className="container mx-auto px-6 max-w-5xl pb-32">
                    <FadeIn delay={0.1}>
                        <div className="relative rounded-[32px] overflow-hidden bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline)]">
                            <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        backgroundImage: 'radial-gradient(circle at 1.5px 1.5px, var(--color-md-on-surface) 1px, transparent 0)',
                                        backgroundSize: '32px 32px',
                                    }}
                                />
                            </div>
                            <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-[var(--color-md-primary)]/[0.06] blur-[100px] pointer-events-none" />
                            <div className="relative z-10 px-8 py-16 md:px-16 md:py-20 text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[var(--color-md-primary)]/[0.1] mb-8 shadow-cinematic-1">
                                    <Download className="w-7 h-7 text-[var(--color-md-primary)]" />
                                </div>
                                <h3 className="text-3xl md:text-4xl font-black text-[var(--color-md-on-surface)] tracking-tight mb-4 leading-tight">
                                    Start exporting now
                                </h3>
                                <p className="text-[var(--color-md-on-surface-variant)] mb-10 max-w-md mx-auto text-lg font-medium leading-relaxed">
                                    Install WaVault and export your WhatsApp contacts in seconds. Free to start.
                                </p>
                                <Button
                                    href={CHROME_STORE_URL}
                                    className="px-10 py-4 rounded-full font-bold text-lg btn-glow shadow-cinematic-3 hover:shadow-cinematic-4 transition-all"
                                >
                                    Add to Chrome — It&apos;s Free
                                </Button>
                            </div>
                        </div>
                    </FadeIn>
                </section>

                <TrustStrip />
                <RelatedUseCases currentPath={currentPath} />
                <TrustSignals />
            </main>
        </>
    );
}
